import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { MetricConfig, MetricId, getMetricScore } from './metricConfig';
import { MeasurementResults, ExportDemographics } from '../../hooks/useResultsExport';
import { BAND_COLORS, scoreBand, markerPosition } from './scoreBands';
import { buttonDefaults } from '../../style/tokens';
import WellnessArc from './WellnessArc';

const CARD_W = 232;
const CARD_H = 300;

// ── Deck ────────────────────────────────────────────────────────────────────

const Deck = styled.div<{ $height: number }>`
  position: relative;
  height: ${({ $height }) => $height}px;
  margin: 4px 14px 0;
  transition: height 0.25s ease;
`;

const Card = styled.div<{ $pos: number; $static?: boolean }>`
  position: absolute;
  top: 0;
  left: 50%;
  width: ${CARD_W}px;
  min-height: ${CARD_H}px;
  height: auto;
  margin-left: -${CARD_W / 2}px;
  background: #fff;
  border-radius: 24px;
  padding: 24px 20px 18px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};
  user-select: none;
  transition: transform 0.38s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.38s;
  /* $pos is signed: positive cards stack to the right (next), negative ones
     mirror to the left (previous), so the deck reads as a loop. */
  ${({ $pos }) => {
    const side = $pos < 0 ? '-' : '';
    switch (Math.abs($pos)) {
      case 0:  return 'transform: translateX(0) scale(1);      opacity: 1;    z-index: 5;';
      case 1:  return `transform: translateX(${side}68%) scale(0.87); opacity: 0.55; z-index: 4;`;
      case 2:  return `transform: translateX(${side}118%) scale(0.73); opacity: 0.32; z-index: 3;`;
      default: return `transform: translateX(${side}148%) scale(0.65); opacity: 0;   z-index: 2; pointer-events: none;`;
    }
  }}
`;

const Title = styled.div`
  font-size: 19px;
  font-weight: 700;
  color: #1a2433;
  text-align: center;
  line-height: 1.25;
  margin-bottom: 6px;
`;

const Gauge = styled.div`
  position: relative;
  margin: 4px 0 10px;
`;

const GaugeIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 78px;
  height: 78px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const StatusButton = styled.button<{ $bg: string; $static?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 16px;
  background: ${({ $bg }) => $bg};
  color: #1a2433;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
`;

const StatusText = styled.span`
  font-size: 16.5px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  word-break: break-word;
`;

const DetailsLink = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

// ── Nav row ─────────────────────────────────────────────────────────────────

const Nav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px 0 4px;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.button<{ $on: boolean }>`
  width: ${({ $on }) => ($on ? '18px' : '6px')};
  height: 6px;
  padding: 0;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $on, theme }) => ($on ? (theme.coloreBottoneAttivo || buttonDefaults.active) : 'rgba(27,61,110,0.22)')};
`;

const Arrow = styled.button`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  border: none;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  font-size: 18px;
  line-height: 1;
  @media (hover: hover) { &:hover:not(:disabled) { color: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; } }
  &:disabled { color: ${p => p.theme.coloreBottoneDisabilitato || '#ccc'}; opacity: 0.4; cursor: default; }
`;

interface MetricDeckProps {
  metrics: MetricConfig[];
  results: MeasurementResults;
  demographics?: ExportDemographics;
  onMetricClick: (id: MetricId) => void;
}

const MetricDeck: React.FC<MetricDeckProps> = ({ metrics, results, demographics, onMetricClick }) => {
  const intl = useIntl();
  const { resultBands, metricIcons } = useTheme();
  const [current, setCurrent] = useState(0);
  const total = metrics.length;
  // A single-indicator instance (e.g. a Burnout-only partner) has nothing to
  // page through and no detail page to open — the report shows that indicator's
  // copy inline instead (see ReportPage). Drop the deck chrome accordingly.
  const single = total === 1;

  const go = (i: number) => setCurrent(((i % total) + total) % total);

  // The deck's height tracks the front card's actual content height (rather
  // than a fixed value) so longer translations don't get clipped or overflow
  // the card's rounded box. Cards are absolutely positioned for the stacking
  // effect, so this must be measured — it isn't derived from normal flow.
  const frontCardRef = useRef<HTMLDivElement | null>(null);
  const [deckHeight, setDeckHeight] = useState(CARD_H);

  useLayoutEffect(() => {
    const el = frontCardRef.current;
    if (!el) return;
    const measure = () => setDeckHeight(Math.max(CARD_H, el.offsetHeight));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [current]);

  // Swipe-to-navigate (touch). A horizontal drag past the threshold advances
  // the deck; `swiped` suppresses the front-card click that fires on touchend
  // so a swipe never opens a metric. Vertical scroll is left untouched (we
  // never preventDefault and only react to horizontal-dominant gestures).
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const swiped = useRef(false);
  const SWIPE_THRESHOLD = 45;

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    swiped.current = false;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - (startY.current ?? 0);
    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) swiped.current = true;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    startX.current = null;
    // Only navigate when the gesture read as a horizontal swipe (see onTouchMove)
    // and cleared the distance threshold — a vertical scroll that drifts sideways
    // must not flip cards. Left → next, right → previous.
    if (swiped.current && Math.abs(dx) >= SWIPE_THRESHOLD) go(dx < 0 ? current + 1 : current - 1);
  };

  return (
    <>
      <Deck $height={deckHeight} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        {metrics.map((config, i) => {
          // Signed distance from the front card, wrapped to the shorter way
          // round: the last card sits at -1 (behind, left) of the first.
          const delta = ((i - current) % total + total) % total;
          const rel = delta > total / 2 ? delta - total : delta;
          const pos = Math.max(-3, Math.min(3, rel));
          const score = getMetricScore(config, results, demographics);
          const available = score !== null;
          // §4.5/§4.9 band on a raw value, not the score (Rule 1) — null elsewhere.
          const rawValue = config.getBandValue?.(results, demographics) ?? null;
          const band = available ? scoreBand(config.id, score!, resultBands, rawValue) : null;
          const position = available ? markerPosition(config.id, score!, resultBands, rawValue) : 50;
          const iconUrl = metricIcons[config.id] || config.icon;

          return (
            <Card
              key={config.id}
              ref={pos === 0 ? frontCardRef : undefined}
              $pos={pos}
              $static={single}
              onClick={single ? undefined : () => {
                if (swiped.current) { swiped.current = false; return; }
                if (pos === 0) onMetricClick(config.id);
                else go(i);
              }}
            >
              <Title>{intl.formatMessage({ id: config.labelKey })}</Title>
              <Gauge>
                <WellnessArc position={position} bands={resultBands} size={124} showMarker={available} />
                {(iconUrl || config.emoji) && (
                  <GaugeIcon>
                    {iconUrl
                      ? <img src={iconUrl} alt="" />
                      : config.emoji}
                  </GaugeIcon>
                )}
              </Gauge>
              <StatusButton
                as={single ? 'div' : 'button'}
                $bg={available && band ? BAND_COLORS[band] : '#eceff1'}
                $static={single}
              >
                <StatusText>
                  {available
                    ? intl.formatMessage({ id: config.getDescriptionKey(score!, rawValue) })
                    : intl.formatMessage({ id: 'metric.ui.unavailable' })}
                </StatusText>
                {!single && (
                  <DetailsLink>{intl.formatMessage({ id: 'metric.ui.clickForDetails' })} ›</DetailsLink>
                )}
              </StatusButton>
            </Card>
          );
        })}
      </Deck>

      {!single && (
        <Nav>
          <Arrow onClick={() => go(current - 1)} aria-label="Previous">‹</Arrow>
          <Dots>
            {metrics.map((config, i) => (
              <Dot key={config.id} $on={i === current} onClick={() => go(i)} aria-label={`Card ${i + 1}`} />
            ))}
          </Dots>
          <Arrow onClick={() => go(current + 1)} aria-label="Next">›</Arrow>
        </Nav>
      )}
    </>
  );
};

export default MetricDeck;
